import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Search, Filter, Calendar, Users } from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { NewBookingDialog } from "@/components/booking/NewBookingDialog";
import { BookingDetailsDialog } from "@/components/booking/BookingDetailsDialog";
import { EditBookingDialog } from "@/components/booking/EditBookingDialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const BookingCenter = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Load bookings from database
  const loadBookings = async () => {
    setLoading(true);
    try {
      // Load package bookings
      const { data: packageBookings, error: packageError } = await supabase
        .from("package_bookings")
        .select(`
          *,
          clients (
            name_ar,
            mobile,
            additional_info
          )
        `)
        .order("created_at", { ascending: false });

      if (packageError) throw packageError;

      // Transform package bookings to match the expected format
      const transformedPackageBookings = packageBookings?.map(booking => ({
        id: booking.id,
        type: "package",
        bookingNumber: booking.package_number || `PKG-${booking.id.slice(0, 8)}`,
        clientName: booking.clients?.name_ar || "غير محدد",
        clientPhone: booking.clients?.mobile,
        service: `الباقة السياحية - مسافر واحد`,
        status: booking.status,
        checkIn: booking.booking_date,
        checkOut: booking.end_date,
        totalAmount: booking.price || 0,
        currency: booking.currency || "SAR",
        destination: "وجهة سياحية",
        passengerCount: 1,
        roomCount: 1,
        notes: booking.notes,
      })) || [];

      setBookings(transformedPackageBookings);
    } catch (error) {
      console.error("Error loading bookings:", error);
      toast.error("حدث خطأ أثناء تحميل الحجوزات");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  // Sample data for demonstration (will be replaced by real data)
  const sampleBookings = [
    {
      id: "1",
      type: "package",
      bookingNumber: "BKG-2501-001",
      clientName: "محمد أحمد",
      service: "رحلة تركيا - 7 أيام",
      status: "confirmed",
      checkIn: "2025-01-30",
      totalAmount: 5000,
      currency: "SAR",
    },
    {
      id: "2", 
      type: "flight",
      bookingNumber: "FLT-2501-002",
      clientName: "فاطمة علي",
      service: "الرياض - إسطنبول",
      status: "pending",
      checkIn: "2025-02-05",
      totalAmount: 1500,
      currency: "SAR",
    },
    {
      id: "3",
      type: "hotel",
      bookingNumber: "HTL-2501-003", 
      clientName: "خالد محمود",
      service: "فندق حياة ريجنسي",
      status: "cancelled",
      checkIn: "2025-01-25",
      totalAmount: 2000,
      currency: "SAR",
    },
  ];

  // Use real bookings if available, otherwise use sample data
  const displayBookings = bookings.length > 0 ? bookings : sampleBookings;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed": return "bg-success/10 text-success border-success/20";
      case "pending": return "bg-warning/10 text-warning border-warning/20";
      case "cancelled": return "bg-destructive/10 text-destructive border-destructive/20";
      default: return "bg-muted";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "confirmed": return "مؤكد";
      case "pending": return "في الانتظار";
      case "cancelled": return "ملغي";
      default: return status;
    }
  };

  const getServiceTypeIcon = (type: string) => {
    switch (type) {
      case "package": return "📦";
      case "flight": return "✈️";
      case "hotel": return "🏨";
      case "transfer": return "🚗";
      default: return "📋";
    }
  };

  const filteredBookings = displayBookings.filter(booking => {
    const matchesSearch = booking.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         booking.bookingNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         booking.service.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = selectedFilter === "all" || booking.status === selectedFilter;
    return matchesSearch && matchesFilter;
  });

  const handleViewDetails = (booking: any) => {
    setSelectedBooking(booking);
    setDetailsDialogOpen(true);
  };

  const handleEditBooking = (booking: any) => {
    setSelectedBooking(booking);
    setEditDialogOpen(true);
  };

  const handleSendConfirmation = async (booking: any) => {
    try {
      // Create booking confirmation record
      const { error } = await supabase
        .from("booking_confirmations")
        .insert({
          booking_id: booking.id,
          booking_type: booking.type,
          confirmation_type: "email",
          recipient_contact: booking.clientPhone,
          delivery_method: "sms",
          subject: `تأكيد حجز ${booking.service}`,
          content: `
            عزيزي/عزيزتي ${booking.clientName},
            
            نؤكد لكم حجز ${booking.service}
            رقم الحجز: ${booking.bookingNumber}
            التاريخ: ${format(new Date(booking.checkIn), "dd MMMM yyyy", { locale: ar })}
            المبلغ: ${booking.totalAmount.toLocaleString()} ${booking.currency}
            
            شكراً لثقتكم بنا.
          `,
          status: "sent",
          sent_at: new Date().toISOString(),
        });

      if (error) throw error;
      toast.success("تم إرسال تأكيد الحجز بنجاح");
    } catch (error) {
      console.error("Error sending confirmation:", error);
      toast.error("حدث خطأ أثناء إرسال التأكيد");
    }
  };

  const stats = {
    total: displayBookings.length,
    confirmed: displayBookings.filter(b => b.status === "confirmed").length,
    pending: displayBookings.filter(b => b.status === "pending").length,
    revenue: displayBookings.reduce((sum, b) => sum + (b.totalAmount || 0), 0),
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">مركز الحجوزات المباشرة</h1>
          <p className="text-muted-foreground mt-1">واجهة موحدة لإدارة جميع أنواع الحجوزات</p>
        </div>
        <NewBookingDialog onBookingCreated={loadBookings}>
          <Button className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            حجز جديد
          </Button>
        </NewBookingDialog>
      </div>

      {/* إحصائيات سريعة */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">إجمالي الحجوزات</p>
                <p className="text-2xl font-bold text-foreground">{stats.total}</p>
              </div>
              <div className="text-2xl">📊</div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">الحجوزات المؤكدة</p>
                <p className="text-2xl font-bold text-success">{stats.confirmed}</p>
              </div>
              <div className="text-2xl">✅</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">في الانتظار</p>
                <p className="text-2xl font-bold text-warning">{stats.pending}</p>
              </div>
              <div className="text-2xl">⏳</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">الإيرادات</p>
                <p className="text-2xl font-bold text-foreground">{stats.revenue.toLocaleString()} ر.س</p>
              </div>
              <div className="text-2xl">💰</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* أدوات البحث والفلترة */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="البحث في الحجوزات..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                variant={selectedFilter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedFilter("all")}
              >
                الكل
              </Button>
              <Button
                variant={selectedFilter === "confirmed" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedFilter("confirmed")}
              >
                مؤكد
              </Button>
              <Button
                variant={selectedFilter === "pending" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedFilter("pending")}
              >
                في الانتظار
              </Button>
              <Button
                variant={selectedFilter === "cancelled" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedFilter("cancelled")}
              >
                ملغي
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* قائمة الحجوزات */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            الحجوزات الحالية
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredBookings.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>لا توجد حجوزات مطابقة للبحث</p>
              </div>
            ) : (
              filteredBookings.map((booking) => (
                <Card key={booking.id} className="border border-border hover:border-primary/20 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="text-2xl">
                          {getServiceTypeIcon(booking.type)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-foreground">{booking.clientName}</h3>
                            <Badge variant="outline" className="text-xs">
                              {booking.bookingNumber}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">{booking.service}</p>
                          <p className="text-xs text-muted-foreground">
                            تاريخ السفر: {format(new Date(booking.checkIn), "dd MMMM yyyy", { locale: ar })}
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-left">
                        <Badge className={getStatusColor(booking.status)}>
                          {getStatusText(booking.status)}
                        </Badge>
                        <p className="text-lg font-bold text-foreground mt-2">
                          {booking.totalAmount.toLocaleString()} {booking.currency}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleViewDetails(booking)}
                      >
                        عرض التفاصيل
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleEditBooking(booking)}
                      >
                        تعديل
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleSendConfirmation(booking)}
                      >
                        إرسال تأكيد
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <BookingDetailsDialog
        booking={selectedBooking}
        open={detailsDialogOpen}
        onOpenChange={setDetailsDialogOpen}
        onEdit={() => {
          setDetailsDialogOpen(false);
          setEditDialogOpen(true);
        }}
        onDelete={() => {
          setDetailsDialogOpen(false);
          loadBookings();
        }}
      />

      <EditBookingDialog
        booking={selectedBooking}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onBookingUpdated={() => {
          setEditDialogOpen(false);
          loadBookings();
        }}
      />
    </div>
  );
};

export default BookingCenter;